import React from 'react';

import { Box, Grid, IconButton, Toolbar, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  enableChat,
  getTypeOfUser,
  userGeneral,
  userPharmacyInfo,
} from '@pharmaplan/common';
import { useNavigate, useLocation } from 'react-router-dom';

import { DateTime } from 'luxon';
import { ReactComponent as ReportsIcon } from '../../../assets/svg/reportsIcon.svg';
import { ReactComponent as ChatIcon } from '../../../assets/svg/chatIcon.svg';
import ChatCount from '../../Admin/AdminToolbar/PrimaryToolbar/ChatCount';
import { useAppSelector } from '../../../hooks/useAppSelector';
import genericClasses from '../../../theme/GenericClasses';
import CustomMenuList from '../../common/CustomMenuList';
import useAddMultiple from '../../../hooks/useAddEvent';
import { Constants, MomentTimeFormats } from '../../../helpers/Constants';
import { reportItems, toolbarItems } from '../config';
import strings from '../../../localization';

import styles from './styles';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { setDialog } from '../../../reducers/dialogReducer';
import PromoDetails from '../../Modals/PromoDetailsModal';
import useProfessional from '../../../hooks/Professional/useProfessional';

const { reports, chat } = Constants.paths;

const NavToolBar = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pharmacyInfo = useAppSelector(userPharmacyInfo);
  const pharmacistGeneral = useAppSelector(userGeneral);

  const { isProfessional } = useProfessional();

  const info = isProfessional ? pharmacistGeneral : pharmacyInfo;

  const { creditsLeft, validUntil } = info ?? {};

  const { disableAddEvents, isAddEvents } = useAddMultiple();

  const isChatEnabled = useAppSelector(enableChat);
  const userType = useAppSelector(getTypeOfUser);
  const luxonDate = DateTime.fromISO(validUntil ?? '', {
    setZone: true,
  });
  const day = luxonDate.toFormat(MomentTimeFormats.day);
  const month = luxonDate.toFormat(MomentTimeFormats.shortMonthName);

  const { pathname } = location ?? {};

  const isReports = pathname === reports;
  const isChat = pathname === chat;

  const {
    bottomNavbar,
    bottomNavbarItemActive,
    bottomNavbarItemIcon,
    iconLabel,
    keyboardIcon,
    iconButtonGridContainer,
    iconButtonRightBorder,
    bottomNavbarItem,
  } = styles;

  const { flex } = genericClasses;

  const openPromoDetails = () => {
    dispatch(setDialog({
      Component: <PromoDetails />,
      showCloseButton: false,
      hideBackground: true,
      maxWidth: 'xs',
      heading: {
        title: strings.promoDetails,
      },
    }));
  };

  return (
    <Toolbar sx={bottomNavbar} disableGutters>
      {/* left toolbar buttons */}
      <Grid xs={6} item sx={flex}>
        {toolbarItems(userType).map((item) => {
          const { key, label, Icon, path, id, subPaths } = item;
          const isActive = pathname === path || subPaths?.includes(pathname);

          return (
            <Grid item key={key} sx={iconButtonGridContainer}>
              <IconButton
                id={id}
                sx={[
                  bottomNavbarItem,
                  isActive && !isAddEvents ? bottomNavbarItemActive : {},
                ]}
                color="inherit"
                size="small"
                onClick={() => {
                  disableAddEvents();
                  navigate(path);
                }}
              >
                <Icon style={bottomNavbarItemIcon} />
                <Typography sx={iconLabel}>{label}</Typography>
              </IconButton>
              <Box component="div" sx={iconButtonRightBorder} />
            </Grid>
          );
        })}

        <CustomMenuList
          id="reports"
          items={reportItems(navigate, userType)}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          disableRipple={false}
          customIconButtonStyle={[
            bottomNavbarItem,
            isReports ? bottomNavbarItemActive : {},
          ]}
        >
          <Grid item sx={iconButtonGridContainer}>
            <ReportsIcon style={bottomNavbarItemIcon} />
            <Typography sx={iconLabel}>{strings.reports}</Typography>
            <KeyboardArrowDownIcon sx={keyboardIcon} />
          </Grid>
        </CustomMenuList>
        {isChatEnabled && (
          <Grid item sx={iconButtonGridContainer}>
            <Box component="div" sx={iconButtonRightBorder} />
            <IconButton
              id="chat"
              sx={[
                bottomNavbarItem,
                isChat && !isAddEvents ? bottomNavbarItemActive : {},
                styles.chatButton,
              ]}
              color="inherit"
              size="small"
              onClick={() => {
                disableAddEvents();
                window.open(`${window.location.origin}${chat}`, '_blank');
              }}
            >
              <ChatIcon style={bottomNavbarItemIcon} />
              <Typography sx={iconLabel}>{strings.chat}</Typography>
              <ChatCount />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {creditsLeft && (
        <Box onClick={openPromoDetails} sx={styles.chip}>
          <Typography sx={styles.chipText}>
            <Typography component="span" sx={styles.celebrationText}>
              🎉
            </Typography>
            {` ${strings.formatString(
              strings.promoPricingFormat,
              `${creditsLeft ?? 0}`,
            )} `}
          </Typography>
          <Typography sx={[styles.hurryText, styles.hurryMargin]}>
            {strings.hurryOfferEndsFormat}
          </Typography>
          <Typography sx={styles.hurryText}>
            {`${day} ${month}`}
          </Typography>
        </Box>
      )}
    </Toolbar>
  );
};

export default NavToolBar;
